import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The small heading over one group on a staff screen: a zone on the floor, a category on a menu, the
 * payments on a bill. One size everywhere, with the group's count beside it and, at the end of the row,
 * an optional link or control. `id` is for the group's `aria-labelledby`; the count is part of the
 * heading's name, written as "Name · n" like before.
 */
export function SectionHeading({
  children,
  id,
  level = 2,
  count,
  action,
  className,
}: {
  children: ReactNode;
  id?: string;
  level?: 2 | 3;
  count?: number;
  action?: ReactNode;
  className?: string;
}) {
  const Tag = level === 2 ? 'h2' : 'h3';
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <Tag id={id} className="min-w-0 text-sm font-semibold break-words text-muted">
        {children}
        {count !== undefined && (
          <span className="font-normal text-faint tabular-nums"> · {count}</span>
        )}
      </Tag>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}
